/**
 * Copy for /business-value (`businessValuePage` namespace) — the business
 * case in plain language plus the ROI calculator.
 *
 * Claims rule: mechanisms and illustrative arithmetic only. Every number the
 * visitor sees comes from their own (editable) inputs; outputs are labelled
 * as an indication, never a promise. Plan prices are NOT duplicated here —
 * the calculator imports them from `src/lib/marketing-pricing.ts`.
 */

export const businessValuePageEn = {
  meta: {
    title: "Business value — what governed AI preparation saves",
    description:
      "Where FixControl saves time and reduces risk: less searching in support, faster fixes in engineering, quality gates before anything ships, and a decision trail for compliance. Includes an ROI calculator on your own numbers.",
  },
  hero: {
    eyebrow: "Business value",
    title: "From customer issue to verified solution,",
    highlight: "in less time",
    subtitle:
      "FixControl brings the right information together per project and lets AI prepare answers and tested changes — inside your rules, approvals and audit trail. This page shows where that saves money, and lets you calculate it on your own numbers.",
    primaryCta: "Calculate your potential value",
    secondaryCta: "Watch the 60-second demo",
  },
  blocks: {
    eyebrow: "Four ways it pays off",
    title: "Where the time and risk actually go",
    description:
      "No promised percentages — mechanisms. Each block names the work that disappears or the risk that gets caught, so you can weigh it against your own numbers below.",
    items: {
      labor: {
        title: "Structural labor savings in support",
        body: "The searching disappears: tickets, earlier conversations and documentation are already connected per project, and a reply arrives as a prepared draft. Your team reviews and approves instead of digging and writing from scratch.",
      },
      recovery: {
        title: "Faster fixes in engineering",
        body: "A technical escalation arrives as an analysed proposal: probable cause, a prepared change and test results from an isolated environment. The engineer on your team starts at review, not at reproduction.",
      },
      governance: {
        title: "Governance and quality before shipping",
        body: "Rules, risk checks and approval gates run before anything reaches a customer of your organization or one of your repositories. That is what prevents the expensive category: unchecked replies and untested changes going out.",
      },
      compliance: {
        title: "Traceability and compliance built in",
        body: "Every decision is recorded: who approved what, on which evidence, from which channel. Audit and compliance questions become an export instead of a reconstruction project.",
      },
    },
  },
  scale: {
    title: "Growing without growing headcount at the same rate",
    body: "When preparation is done by AI and people spend their time on decisions, the same team handles more tickets and more changes. Growth then shows up as more approved work per person — not automatically as more hires.",
    avoidedTitle: "And the costs that never hit the books",
    avoided:
      "Avoided incidents are invisible in a spreadsheet but real in the outcome: the untested change that never reached production, the wrong reply a reviewer caught, the deployment that waited for a recorded decision. Governance is what keeps those off your incident list.",
  },
  calc: {
    eyebrow: "ROI calculator",
    title: "Calculate it on your own numbers",
    description:
      "Every field is your assumption — adjust it to your situation. The result is an indication based on your input, not a promise. The estimated cost uses the public plan prices.",
    inputsTitle: "Your situation",
    inputs: {
      tickets: { label: "Support tickets per month", hint: "" },
      escalationPct: { label: "Technical escalations", hint: "share of tickets that needs engineering" },
      supportRate: { label: "Support cost per hour (€)", hint: "" },
      devRate: { label: "Engineering cost per hour (€)", hint: "" },
      searchMin: { label: "Search & context time per ticket (min)", hint: "time spent finding history, systems and documentation today" },
      fixHours: { label: "Handling time per escalation (hours)", hint: "current engineering time from report to fix" },
      acceptancePct: { label: "Expected acceptance rate", hint: "share of prepared replies and changes your team accepts" },
      fixSavePct: { label: "Time saved on an accepted change", hint: "share of the handling time an analysed, tested proposal removes" },
    },
    outputsTitle: "What you save with these numbers",
    headlineLabel: "Your estimated saving",
    perMonth: "per month",
    perYear: "that is ≈ {amount} per year",
    sumTitle: "How that amount adds up",
    rows: {
      support: "Support spends ± {hours} h less on digging through history",
      engineering: "Engineering spends ± {hours} h less on escalations",
      cost: "Minus the FixControl subscription ({plan})",
      net: "Saving per month",
    },
    breakEvenPlain: "From ± {tickets} tickets per month the subscription pays for itself.",
    paybackPlain: "After ± {days} days of a month, the monthly price is covered.",
    negativePlain:
      "With these assumptions the saving does not exceed the subscription yet — try adjusting the ticket volume or acceptance rate.",
    planNote: "Estimated plan at this volume: {plan} — prices from the public pricing page.",
    formulaTitle: "How this is calculated",
    formulaBody:
      "Support saving = the prepared replies your team accepts × the search time that disappears per ticket × your support rate. Engineering saving = the accepted escalations × the hours an analysed, tested proposal removes × your engineering rate. From that we subtract the public plan price that fits your volume (≈ 2 AI actions per ticket, plus 1 per escalation). It stays an indication — your real numbers depend on your tickets and on what your team accepts.",
    disclaimer:
      "Indicative calculation on your own assumptions — not a quote and not a guaranteed saving. Start free or book a demo to test it on your real tickets.",
  },
  cta: {
    title: "Test the numbers on your own tickets",
    description:
      "Start free with support work, or book a demo and see the flow from your customer’s report to an approved, tested change on your own data.",
    primary: "Start free",
    secondary: "Book a demo",
  },
};

export const businessValuePageNl = {
  meta: {
    title: "Businesswaarde — wat gecontroleerde AI-voorbereiding oplevert",
    description:
      "Waar FixControl tijd bespaart en risico verkleint: minder zoekwerk in support, sneller herstel in engineering, kwaliteitspoorten vóór verzending en een besluitspoor voor compliance. Met ROI-calculator op je eigen cijfers.",
  },
  hero: {
    eyebrow: "Businesswaarde",
    title: "Van klantmelding naar geverifieerde oplossing,",
    highlight: "in minder tijd",
    subtitle:
      "FixControl brengt per project de juiste informatie samen en laat AI antwoorden en geteste wijzigingen voorbereiden — binnen je regels, goedkeuringen en auditspoor. Deze pagina laat zien waar dat geld oplevert, en rekent het door op je eigen cijfers.",
    primaryCta: "Bereken je potentiële waarde",
    secondaryCta: "Bekijk de demo van 60 seconden",
  },
  blocks: {
    eyebrow: "Vier manieren waarop het zich terugbetaalt",
    title: "Waar de tijd en het risico werkelijk zitten",
    description:
      "Geen beloofde percentages — mechanismen. Elk blok benoemt het werk dat verdwijnt of het risico dat wordt afgevangen, zodat je het hieronder tegen je eigen cijfers kunt afzetten.",
    items: {
      labor: {
        title: "Structurele arbeidsbesparing in support",
        body: "Het zoekwerk verdwijnt: tickets, eerdere gesprekken en documentatie zijn per project al gekoppeld, en een antwoord komt binnen als voorbereid concept. Je team beoordeelt en keurt goed, in plaats van graven en vanaf nul schrijven.",
      },
      recovery: {
        title: "Sneller foutherstel in engineering",
        body: "Een technische escalatie komt binnen als geanalyseerd voorstel: waarschijnlijke oorzaak, een voorbereide wijziging en testresultaten uit een geïsoleerde omgeving. De engineer in je team begint bij review, niet bij reproductie.",
      },
      governance: {
        title: "Governance en kwaliteit vóór verzending",
        body: "Regels, risicocontroles en goedkeuringspoorten draaien voordat er iets een klant van je organisatie of een van je repositories bereikt. Dat voorkomt de dure categorie: ongecontroleerde antwoorden en ongeteste wijzigingen die naar buiten gaan.",
      },
      compliance: {
        title: "Traceability en compliance ingebouwd",
        body: "Elke beslissing is vastgelegd: wie wat goedkeurde, op welk bewijs, vanuit welk kanaal. Audit- en compliancevragen worden een export in plaats van een reconstructieproject.",
      },
    },
  },
  scale: {
    title: "Groeien zonder evenredig mee te groeien in personeel",
    body: "Wanneer AI het voorbereidende werk doet en mensen hun tijd aan beslissingen besteden, verwerkt hetzelfde team meer tickets en meer wijzigingen. Groei vertaalt zich dan in meer goedgekeurd werk per persoon — niet automatisch in meer vacatures.",
    avoidedTitle: "En de kosten die nooit in de boeken komen",
    avoided:
      "Vermeden incidenten zijn onzichtbaar in een spreadsheet maar echt in de uitkomst: de ongeteste wijziging die productie nooit haalde, het verkeerde antwoord dat een reviewer onderschepte, de deployment die op een vastgelegde beslissing wachtte. Governance houdt die van je incidentenlijst.",
  },
  calc: {
    eyebrow: "ROI-calculator",
    title: "Reken het door op je eigen cijfers",
    description:
      "Elk veld is jouw aanname — pas het aan naar je situatie. De uitkomst is een indicatie op basis van je invoer, geen belofte. De geschatte kosten gebruiken de openbare planprijzen.",
    inputsTitle: "Jouw situatie",
    inputs: {
      tickets: { label: "Supporttickets per maand", hint: "" },
      escalationPct: { label: "Technische escalaties", hint: "aandeel tickets dat naar engineering gaat" },
      supportRate: { label: "Supportkosten per uur (€)", hint: "" },
      devRate: { label: "Ontwikkelkosten per uur (€)", hint: "" },
      searchMin: { label: "Zoek- en contexttijd per ticket (min)", hint: "tijd die nu opgaat aan historie, systemen en documentatie vinden" },
      fixHours: { label: "Behandeltijd per escalatie (uur)", hint: "huidige engineeringtijd van melding tot fix" },
      acceptancePct: { label: "Verwachte acceptatiegraad", hint: "aandeel voorbereide antwoorden en wijzigingen dat je team accepteert" },
      fixSavePct: { label: "Tijdsbesparing op een geaccepteerde wijziging", hint: "aandeel van de behandeltijd dat een geanalyseerd, getest voorstel wegneemt" },
    },
    outputsTitle: "Wat je met deze cijfers bespaart",
    headlineLabel: "Jouw geschatte besparing",
    perMonth: "per maand",
    perYear: "dat is ≈ {amount} per jaar",
    sumTitle: "Zo komt dat bedrag tot stand",
    rows: {
      support: "Support is ± {hours} uur minder kwijt aan uitzoekwerk",
      engineering: "Engineering is ± {hours} uur minder kwijt aan escalaties",
      cost: "Min het FixControl-abonnement ({plan})",
      net: "Besparing per maand",
    },
    breakEvenPlain: "Vanaf ± {tickets} tickets per maand verdient het abonnement zichzelf terug.",
    paybackPlain: "Na ± {days} dagen van een maand is de maandprijs terugverdiend.",
    negativePlain:
      "Met deze aannames komt de besparing nog niet boven het abonnement uit — pas bijvoorbeeld het ticketvolume of de acceptatiegraad aan.",
    planNote: "Geschat plan bij dit volume: {plan} — prijzen van de openbare prijzenpagina.",
    formulaTitle: "Zo is dit berekend",
    formulaBody:
      "Supportbesparing = de conceptantwoorden die je team accepteert × de zoektijd die daarmee per ticket wegvalt × je supporttarief. Engineeringbesparing = de geaccepteerde escalaties × de uren die een geanalyseerd, getest voorstel scheelt × je ontwikkeltarief. Daar gaat de openbare planprijs vanaf die bij je volume past (≈ 2 AI-acties per ticket, plus 1 per escalatie). Het blijft een indicatie — je echte cijfers hangen af van je tickets en van wat je team accepteert.",
    disclaimer:
      "Indicatieve berekening op je eigen aannames — geen offerte en geen gegarandeerde besparing. Start gratis of boek een demo om het op je echte tickets te toetsen.",
  },
  cta: {
    title: "Toets de cijfers op je eigen tickets",
    description:
      "Begin gratis met supportwerk, of boek een demo en zie de flow van de melding van je klant tot een goedgekeurde, geteste wijziging op je eigen data.",
    primary: "Begin gratis",
    secondary: "Boek een demo",
  },
};
